import { useMemo } from "react";
import { buildImageInventory, buildSiteStats, imageStandards, ROUTES } from "@/lib/panelData";
import ImageInventory from "../components/ImageInventory";
import PaletteTypography from "../components/PaletteTypography";
import { PanelCard, PanelBadge, ReadOnlyNotice } from "../components/PanelUI";
import { panelModes } from "@/mocks/panelContent";

export default function SiteSheetSection() {
  const inventory = useMemo(() => buildImageInventory(), []);
  const stats = useMemo(() => buildSiteStats(inventory), []);

  return (
    <div className="flex flex-col gap-6">
      <ReadOnlyNotice text={panelModes.viewNotice} />

      <PanelCard
        title="Ficha do site"
        description="Ficha técnica completa da página Amora Belle, calculada a partir do catálogo atual."
      >
        <div className="flex flex-col gap-6">
          {stats.map((group) => (
            <div key={group.title}>
              <h3 className="mb-3 flex items-center gap-2 text-sm font-medium text-foreground-800">
                <span className="flex h-5 w-5 items-center justify-center text-primary-600">
                  <i className="ri-folder-chart-line text-base" />
                </span>
                {group.title}
              </h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {group.items.map((item) => (
                  <div key={item.label} className="rounded-lg border border-background-200 bg-background-100 p-4">
                    <p className="text-[11px] uppercase tracking-[0.14em] text-foreground-500">{item.label}</p>
                    <p className="mt-1 font-heading text-xl text-foreground-900">{item.value}</p>
                    {item.hint ? <p className="mt-0.5 text-[11px] text-foreground-500">{item.hint}</p> : null}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </PanelCard>

      <PanelCard
        title="Rotas cadastradas"
        description="Páginas navegáveis do site no momento."
        action={<PanelBadge tone="primary">{ROUTES.length} rotas</PanelBadge>}
      >
        <ul className="flex flex-wrap gap-2">
          {ROUTES.map((route) => (
            <li key={route} className="rounded-md border border-background-200 bg-background-100 px-3 py-1.5 text-xs text-foreground-700">
              {route}
            </li>
          ))}
        </ul>
      </PanelCard>

      <PanelCard
        title="Paleta e tipografia"
        description="Famílias de cores e fontes usadas no projeto, lidas das variáveis reais do site."
      >
        <PaletteTypography />
      </PanelCard>

      <PanelCard
        title="Padrões aprovados de imagem"
        description="Referência de dimensão e proporção para cada uso."
      >
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-background-300 text-left text-xs uppercase tracking-[0.12em] text-foreground-500">
                <th className="py-2 pr-4 font-medium">Uso da imagem</th>
                <th className="py-2 pr-4 font-medium">Qtd. atual</th>
                <th className="py-2 pr-4 font-medium">Dimensão padrão</th>
                <th className="py-2 font-medium">Proporção</th>
              </tr>
            </thead>
            <tbody>
              {imageStandards.map((standard) => (
                <tr key={standard.key} className="border-b border-background-200 last:border-0">
                  <td className="py-2.5 pr-4 text-foreground-800">{standard.label}</td>
                  <td className="py-2.5 pr-4 text-foreground-600">{standard.quantity}</td>
                  <td className="py-2.5 pr-4 text-foreground-600">
                    {standard.width} × {standard.height} px
                  </td>
                  <td className="py-2.5 text-foreground-600">{standard.ratio}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 rounded-md border border-background-200 bg-background-100 px-3 py-2 text-xs leading-relaxed text-foreground-600">
          Uploads devem aceitar JPG, PNG e WebP (WebP otimizado recomendado), preservar a qualidade,
          alertar quando a proporção estiver incorreta, permitir ajustar o ponto focal e nunca
          distorcer a imagem. Sempre há prévia antes de salvar.
        </p>
      </PanelCard>

      <PanelCard
        title="Inventário de imagens"
        description="Todas as imagens do site, na ordem em que aparecem, com miniatura e padrão de formato."
        action={<PanelBadge tone="secondary">{inventory.length} imagens</PanelBadge>}
      >
        <ImageInventory />
      </PanelCard>
    </div>
  );
}